"use client";

import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, RefreshCcw } from "lucide-react";
import { Area, AreaChart, CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";

import { gameMasterAnalyticaGet } from "@/lib/gameMasterAnalytica";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from "@/components/ui/chart";

const DEFAULT_TZ = "Asia/Kuala_Lumpur";
const TZ_OFFSET = "+08:00";

type MissionsSummaryRow = {
  mission_id: number;
  code: string;
  title: string;
  max_per_day: number | null;
  current_from: string;
  current_to: string;
  previous_from: string;
  previous_to: string;
  cur_users: number;
  cur_completions: number;
  cur_reach_pct: number | null;
  cur_completions_per_user: number | null;
  prev_users: number;
  prev_completions: number;
  prev_reach_pct: number | null;
  prev_completions_per_user: number | null;
  users_delta_pct: number | null;
  completions_delta_pct: number | null;
};

type MissionsSummaryResponse = {
  month: string | null;
  compare: string;
  tz: string;
  items: MissionsSummaryRow[];
};

type TimeseriesRow = {
  day: string; // YYYY-MM-DD
  mission_id: number;
  code: string;
  title: string;
  completions: number;
  users: number;
};

type TimeseriesResponse = {
  range: { from: string; to: string; tz: string };
  items: TimeseriesRow[];
};

type ConsistencyRow = {
  mission_id: number;
  code: string;
  title: string;
  days_in_period: number;
  half_threshold: number;
  per_day_target: number;
  users_at_least_once: number;
  users_at_least_half_days: number;
  users_every_day: number;
};

type ConsistencyResponse = {
  range: { from: string; to: string; tz: string };
  items: ConsistencyRow[];
};

type DiagnosticsRow = {
  http_path: string;
  total: number;
  errors: number;
  error_rate_pct: number | null;
  p95_latency_ms: number | null;
};

type DiagnosticsResponse = {
  range: { from: string; to: string; tz: string };
  items: DiagnosticsRow[];
};

type UsersSummaryResponse = {
  month: string | null;
  tz: string;
  period_from: string;
  period_to: string;
  total_users: number;
  newly_added_users: number;
};

function pad2(n: number) {
  return String(n).padStart(2, "0");
}

function getTzYearMonthNow(tz: string): { year: number; monthIndex: number } {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());

  const year = Number(parts.find((p) => p.type === "year")?.value);
  const month = Number(parts.find((p) => p.type === "month")?.value);
  return { year, monthIndex: month - 1 };
}

function formatMonthId(d: { year: number; monthIndex: number }) {
  return `${d.year}-${pad2(d.monthIndex + 1)}`;
}

function parseMonthId(monthId: string): { year: number; monthIndex: number } | null {
  const m = String(monthId || "").trim();
  const match = /^([0-9]{4})-([0-9]{2})$/.exec(m);
  if (!match) return null;
  const year = Number(match[1]);
  const mon = Number(match[2]);
  if (!Number.isFinite(year) || !Number.isFinite(mon) || mon < 1 || mon > 12) return null;
  return { year, monthIndex: mon - 1 };
}

function addMonths(monthId: string, delta: number): string {
  const parsed = parseMonthId(monthId);
  if (!parsed) return monthId;
  const d = new Date(Date.UTC(parsed.year, parsed.monthIndex + delta, 1));
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}`;
}

function rangeForMonth(monthId: string, tz: string): { fromIso: string; toIso: string } {
  const parsed = parseMonthId(monthId);
  if (!parsed) {
    const now = new Date();
    return { fromIso: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString(), toIso: now.toISOString() };
  }

  // Use tz local boundary strings (KL is fixed +08:00 for our use case).
  const start = new Date(`${parsed.year}-${pad2(parsed.monthIndex + 1)}-01T00:00:00${TZ_OFFSET}`);
  const nextMonth = new Date(Date.UTC(parsed.year, parsed.monthIndex + 1, 1));
  const nextMonthId = `${nextMonth.getUTCFullYear()}-${pad2(nextMonth.getUTCMonth() + 1)}`;
  const nextParsed = parseMonthId(nextMonthId)!;
  const endExclusive = new Date(`${nextParsed.year}-${pad2(nextParsed.monthIndex + 1)}-01T00:00:00${TZ_OFFSET}`);

  // If selected month is current month in tz, end at now (so charts feel alive).
  const cur = formatMonthId(getTzYearMonthNow(tz));
  const to = cur === monthId ? new Date() : endExclusive;

  return { fromIso: start.toISOString(), toIso: to.toISOString() };
}

function fmtCompact(n: number | null | undefined) {
  const x = typeof n === "number" && Number.isFinite(n) ? n : null;
  if (x === null) return "—";
  return x.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function fmtPct(n: number | null | undefined) {
  const x = typeof n === "number" && Number.isFinite(n) ? n : null;
  if (x === null) return "—";
  return `${x.toFixed(2)}%`;
}

function DeltaPill({ value }: { value: number | null }) {
  if (value === null || !Number.isFinite(value)) return <Badge variant="outline" className="font-normal">—</Badge>;
  const up = value >= 0;
  return (
    <Badge variant={up ? "default" : "secondary"} className="font-normal">
      {up ? "+" : ""}
      {value.toFixed(1)}%
    </Badge>
  );
}

function stableColorForIndex(i: number) {
  const tokens = ["--chart-1", "--chart-2", "--chart-3", "--chart-4", "--chart-5"];
  return `var(${tokens[i % tokens.length]})`;
}

export function GameMasterAnalyticaPage() {
  const [tz, setTz] = useState(DEFAULT_TZ);
  const [monthId, setMonthId] = useState(() => formatMonthId(getTzYearMonthNow(DEFAULT_TZ)));
  const [compareMtd, setCompareMtd] = useState(true);
  const [tab, setTab] = useState<"engagement" | "diagnostics">("engagement");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [usersSummary, setUsersSummary] = useState<UsersSummaryResponse | null>(null);
  const [summary, setSummary] = useState<MissionsSummaryResponse | null>(null);
  const [timeseries, setTimeseries] = useState<TimeseriesResponse | null>(null);
  const [consistency, setConsistency] = useState<ConsistencyResponse | null>(null);
  const [diagnostics, setDiagnostics] = useState<DiagnosticsResponse | null>(null);

  const range = useMemo(() => rangeForMonth(monthId, tz), [monthId, tz]);
  const compareMode = compareMtd ? "previous_mtd" : "previous_month";

  const missionOrder = useMemo(() => {
    const items = summary?.items || [];
    return [...items].sort((a, b) => (b.cur_completions || 0) - (a.cur_completions || 0));
  }, [summary]);

  const chartConfig = useMemo(() => {
    const cfg: ChartConfig = {};
    missionOrder.forEach((m, idx) => {
      cfg[m.code] = { label: m.code, color: stableColorForIndex(idx) };
    });
    cfg.total = { label: "Total", color: "var(--chart-3)" };
    return cfg;
  }, [missionOrder]);

  const dailyChartData = useMemo(() => {
    const rows = timeseries?.items || [];
    // day -> code -> values
    const map = new Map<string, Record<string, any>>();
    for (const r of rows) {
      const day = String((r as any).day ?? "");
      if (!day) continue;
      const cur = map.get(day) ?? { day };
      cur[r.code] = Number(r.completions ?? 0) || 0;
      cur[`${r.code}__users`] = Number(r.users ?? 0) || 0;
      map.set(day, cur);
    }
    const out = Array.from(map.values()).sort((a, b) => String(a.day).localeCompare(String(b.day)));
    // total completions per day
    for (const obj of out) {
      let total = 0;
      for (const m of missionOrder) total += Number(obj[m.code] ?? 0) || 0;
      obj.total = total;
    }
    return out;
  }, [timeseries, missionOrder]);

  const overallKpis = useMemo(() => {
    const items = summary?.items || [];
    const totalCompletions = items.reduce((acc, it) => acc + (Number(it.cur_completions) || 0), 0);
    const totalUsersNonDistinct = items.reduce((acc, it) => acc + (Number(it.cur_users) || 0), 0);
    const avgReachPct = items.length
      ? items.reduce((acc, it) => acc + (Number(it.cur_reach_pct) || 0), 0) / items.length
      : null;
    return { totalCompletions, totalUsersNonDistinct, avgReachPct };
  }, [summary]);

  async function loadAll() {
    setLoading(true);
    setError(null);
    try {
      const [u, s, ts, c, d] = await Promise.all([
        gameMasterAnalyticaGet<UsersSummaryResponse>("users/summary", {
          month: monthId,
          tz,
        }),
        gameMasterAnalyticaGet<MissionsSummaryResponse>("missions/summary", {
          month: monthId,
          compare: compareMode,
          tz,
        }),
        gameMasterAnalyticaGet<TimeseriesResponse>("missions/timeseries", {
          from: range.fromIso,
          to: range.toIso,
          tz,
        }),
        gameMasterAnalyticaGet<ConsistencyResponse>("missions/consistency", {
          from: range.fromIso,
          to: range.toIso,
          tz,
        }),
        gameMasterAnalyticaGet<DiagnosticsResponse>("missions/diagnostics", {
          from: range.fromIso,
          to: range.toIso,
          tz,
        }),
      ]);
      setUsersSummary(u);
      setSummary(s);
      setTimeseries(ts);
      setConsistency(c);
      setDiagnostics(d);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load Game Master Analytica.");
      setUsersSummary(null);
      setSummary(null);
      setTimeseries(null);
      setConsistency(null);
      setDiagnostics(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthId, compareMode, tz]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <CardTitle>Game Master Analytica</CardTitle>
            <CardDescription>Mission engagement, month-over-month (and MTD) insights.</CardDescription>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-center gap-2">
              <Label className="text-sm font-normal text-muted-foreground">Month</Label>
              <Select value={monthId} onValueChange={setMonthId}>
                <SelectTrigger className="w-[140px] h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {/* simple: show last 6 months */}
                  {Array.from({ length: 6 }).map((_, i) => {
                    const id = addMonths(formatMonthId(getTzYearMonthNow(tz)), -i);
                    return (
                      <SelectItem key={id} value={id}>
                        {id}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Switch id="gma-compare-mtd" checked={compareMtd} onCheckedChange={(v) => setCompareMtd(Boolean(v))} />
              <Label htmlFor="gma-compare-mtd" className="text-sm font-normal text-muted-foreground">
                Compare MTD
              </Label>
            </div>

            <div className="flex items-center gap-2">
              <Label className="text-sm font-normal text-muted-foreground">TZ</Label>
              <Select value={tz} onValueChange={setTz}>
                <SelectTrigger className="w-[190px] h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Asia/Kuala_Lumpur">Asia/Kuala_Lumpur</SelectItem>
                  <SelectItem value="UTC">UTC</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button variant="outline" className="h-9 font-normal" onClick={loadAll} disabled={loading}>
              <RefreshCcw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardHeader>
      </Card>

      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Game Master Analytica failed to load</AlertTitle>
          <AlertDescription className="break-words">{error}</AlertDescription>
        </Alert>
      )}

      {/* Pulse KPIs (note: users are non-distinct across missions) */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {loading || !summary || !usersSummary ? (
          <>
            <Skeleton className="h-[96px]" />
            <Skeleton className="h-[96px]" />
            <Skeleton className="h-[96px]" />
            <Skeleton className="h-[96px]" />
            <Skeleton className="h-[96px]" />
          </>
        ) : (
          <>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Total users</CardDescription>
                <CardTitle className="text-2xl">{fmtCompact(usersSummary.total_users)}</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <Badge variant="outline" className="font-normal">
                  As of {usersSummary.period_to.slice(0, 10)}
                </Badge>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>New users (this month)</CardDescription>
                <CardTitle className="text-2xl">{fmtCompact(usersSummary.newly_added_users)}</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <Badge variant="secondary" className="font-normal">
                  {usersSummary.period_from.slice(0, 10)} → {usersSummary.period_to.slice(0, 10)}
                </Badge>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Total completions</CardDescription>
                <CardTitle className="text-2xl">{fmtCompact(overallKpis.totalCompletions)}</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <Badge variant="outline" className="font-normal">
                  Range: {range.fromIso.slice(0, 10)} → {range.toIso.slice(0, 10)}
                </Badge>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Mission users (non-distinct)</CardDescription>
                <CardTitle className="text-2xl">{fmtCompact(overallKpis.totalUsersNonDistinct)}</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <Badge variant="secondary" className="font-normal">
                  Sum of per-mission distinct users
                </Badge>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Avg reach % (per mission)</CardDescription>
                <CardTitle className="text-2xl">{fmtPct(overallKpis.avgReachPct)}</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <Badge variant="outline" className="font-normal">
                  Compare: {compareMode}
                </Badge>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as any)}>
        <TabsList className="flex flex-wrap">
          <TabsTrigger value="engagement">Engagement</TabsTrigger>
          <TabsTrigger value="diagnostics">Diagnostics</TabsTrigger>
        </TabsList>

        <TabsContent value="engagement">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Mission Pulse (Completions)</CardTitle>
                <CardDescription>Stacked daily completions by mission.</CardDescription>
              </CardHeader>
              <CardContent>
                {loading || !timeseries || missionOrder.length === 0 ? (
                  <Skeleton className="h-[280px] w-full" />
                ) : (
                  <ChartContainer config={chartConfig} className="h-[280px] w-full">
                    <AreaChart data={dailyChartData} margin={{ left: 8, right: 8, top: 8, bottom: 0 }}>
                      <CartesianGrid vertical={false} />
                      <XAxis dataKey="day" tickLine={false} axisLine={false} minTickGap={24} tickFormatter={(v) => String(v).slice(5)} />
                      <YAxis tickLine={false} axisLine={false} width={40} />
                      <ChartTooltip content={<ChartTooltipContent indicator="dot" />} />
                      {missionOrder.slice(0, 5).map((m) => (
                        <Area
                          key={m.code}
                          type="monotone"
                          dataKey={m.code}
                          stackId="a"
                          stroke={`var(--color-${m.code})`}
                          fill={`var(--color-${m.code})`}
                          fillOpacity={0.22}
                        />
                      ))}
                    </AreaChart>
                  </ChartContainer>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Total Completions Trend</CardTitle>
                <CardDescription>Overall daily completions (all missions combined).</CardDescription>
              </CardHeader>
              <CardContent>
                {loading || !timeseries ? (
                  <Skeleton className="h-[280px] w-full" />
                ) : (
                  <ChartContainer config={chartConfig} className="h-[280px] w-full">
                    <LineChart data={dailyChartData} margin={{ left: 8, right: 8, top: 8, bottom: 0 }}>
                      <CartesianGrid vertical={false} />
                      <XAxis dataKey="day" tickLine={false} axisLine={false} minTickGap={24} tickFormatter={(v) => String(v).slice(5)} />
                      <YAxis tickLine={false} axisLine={false} width={40} />
                      <ChartTooltip content={<ChartTooltipContent indicator="line" />} />
                      <Line type="monotone" dataKey="total" stroke="var(--color-total)" dot={false} />
                    </LineChart>
                  </ChartContainer>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Mission Leaderboard</CardTitle>
                <CardDescription>Compare mission performance across periods.</CardDescription>
              </CardHeader>
              <CardContent>
                {loading || !summary ? (
                  <Skeleton className="h-[300px] w-full" />
                ) : (
                  <div className="rounded-md border overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Mission</TableHead>
                          <TableHead className="text-right">Users</TableHead>
                          <TableHead className="text-right">Completions</TableHead>
                          <TableHead className="text-right">Reach %</TableHead>
                          <TableHead className="text-right">C/User</TableHead>
                          <TableHead className="text-right">Δ Users</TableHead>
                          <TableHead className="text-right">Δ Comp</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {missionOrder.map((m) => (
                          <TableRow key={m.code}>
                            <TableCell className="text-sm">
                              <div className="font-medium">{m.title}</div>
                              <div className="text-xs text-muted-foreground flex items-center gap-2">
                                <Badge variant="outline" className="font-normal">
                                  {m.code}
                                </Badge>
                                {m.max_per_day ? (
                                  <span className="font-mono">max/day {m.max_per_day}</span>
                                ) : null}
                              </div>
                            </TableCell>
                            <TableCell className="text-right text-sm font-mono tabular-nums">{fmtCompact(m.cur_users)}</TableCell>
                            <TableCell className="text-right text-sm font-mono tabular-nums">{fmtCompact(m.cur_completions)}</TableCell>
                            <TableCell className="text-right text-sm font-mono tabular-nums">{fmtPct(m.cur_reach_pct)}</TableCell>
                            <TableCell className="text-right text-sm font-mono tabular-nums">{fmtCompact(m.cur_completions_per_user)}</TableCell>
                            <TableCell className="text-right text-sm">
                              <DeltaPill value={m.users_delta_pct} />
                            </TableCell>
                            <TableCell className="text-right text-sm">
                              <DeltaPill value={m.completions_delta_pct} />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Engagement Depth</CardTitle>
                <CardDescription>Consistency KPIs generalized per mission.</CardDescription>
              </CardHeader>
              <CardContent>
                {loading || !consistency ? (
                  <Skeleton className="h-[300px] w-full" />
                ) : (
                  <div className="rounded-md border overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Mission</TableHead>
                          <TableHead className="text-right">At least once</TableHead>
                          <TableHead className="text-right">≥ Half days</TableHead>
                          <TableHead className="text-right">Every day</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {consistency.items
                          .slice()
                          .sort((a, b) => (b.users_at_least_once || 0) - (a.users_at_least_once || 0))
                          .map((r) => (
                            <TableRow key={r.code}>
                              <TableCell className="text-sm">
                                <div className="font-medium">{r.title}</div>
                                <div className="text-xs text-muted-foreground">
                                  <span className="font-mono">{r.code}</span> · target/day {r.per_day_target} · {r.days_in_period} days
                                </div>
                              </TableCell>
                              <TableCell className="text-right text-sm font-mono tabular-nums">{fmtCompact(r.users_at_least_once)}</TableCell>
                              <TableCell className="text-right text-sm font-mono tabular-nums">{fmtCompact(r.users_at_least_half_days)}</TableCell>
                              <TableCell className="text-right text-sm font-mono tabular-nums">{fmtCompact(r.users_every_day)}</TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="diagnostics">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Mission Diagnostics</CardTitle>
              <CardDescription>Endpoint health signals affecting mission completion.</CardDescription>
            </CardHeader>
            <CardContent>
              {loading || !diagnostics ? (
                <Skeleton className="h-[260px] w-full" />
              ) : diagnostics.items.length === 0 ? (
                <div className="text-sm text-muted-foreground">No diagnostics data.</div>
              ) : (
                <div className="rounded-md border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>HTTP Path</TableHead>
                        <TableHead className="text-right">Calls</TableHead>
                        <TableHead className="text-right">Errors</TableHead>
                        <TableHead className="text-right">Error %</TableHead>
                        <TableHead className="text-right">p95 (ms)</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {diagnostics.items.map((r) => (
                        <TableRow key={r.http_path}>
                          <TableCell className="text-sm font-mono max-w-[520px] truncate">{r.http_path}</TableCell>
                          <TableCell className="text-right text-sm font-mono tabular-nums">{fmtCompact(r.total)}</TableCell>
                          <TableCell className="text-right text-sm font-mono tabular-nums">{fmtCompact(r.errors)}</TableCell>
                          <TableCell className="text-right text-sm font-mono tabular-nums">{fmtPct(r.error_rate_pct)}</TableCell>
                          <TableCell className="text-right text-sm font-mono tabular-nums">{fmtCompact(r.p95_latency_ms)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

